import { useQuery } from '@tanstack/react-query';
import { getMaintenanceStatus, getConfig } from '@/api/config';
import {
  featuresConfigSchema,
  maintenanceConfigSchema,
  type AppConfig,
  type MaintenanceConfig,
} from '@/types/config';

/**
 * Хук для получения статуса maintenance с поллингом каждые 30 секунд
 */
export function useMaintenanceStatus() {
  return useQuery<MaintenanceConfig>({
    queryKey: ['config', 'maintenance'],
    queryFn: () => getMaintenanceStatus(),
    staleTime: 0, // Всегда проверяем актуальность
    refetchInterval: 30000, // Поллинг каждые 30 секунд
    refetchIntervalInBackground: true, // Продолжать поллинг даже когда вкладка неактивна
    retry: 3, // Повторять при ошибке
    retryDelay: 5000, // Задержка между попытками
  });
}

function parseAppConfig(raw: unknown): AppConfig {
  const record = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    maintenance: maintenanceConfigSchema.parse(
      record.maintenance ?? { enabled: false, message: null },
    ),
    features: featuresConfigSchema.parse(record.features ?? {}),
  };
}

/**
 * Хук для получения всей конфигурации приложения
 */
export function useAppConfig() {
  return useQuery<AppConfig>({
    queryKey: ['config'],
    queryFn: async () => parseAppConfig(await getConfig()),
    staleTime: 60000, // Кешируем на 1 минуту
    retry: 3,
  });
}

/**
 * Runtime Progress & Trophies UI gate (`features.gamification`).
 * Missing/invalid config → false (UI hidden; backend finalization stays on).
 */
export function useGamificationFeatureEnabled(): boolean {
  const { data } = useAppConfig();
  return data?.features.gamification === true;
}

